import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useAuthStore } from './auth';
import { sandbox } from './sandbox';
import { isProdHost } from './host';

const SBU_KEY = '3wi.active_sbu';
const ENTITY_KEY = '3wi.active_entity';
const CONTEXT_KEY = '3wi.context_mode'; // 'user' (all my entities + personal) | 'entity' (scoped)

export type ContextMode = 'user' | 'entity';
function readContextMode(): ContextMode {
  if (typeof window === 'undefined') return 'entity';
  return localStorage.getItem(CONTEXT_KEY) === 'user' ? 'user' : 'entity';
}

export type Tier = 'field' | 'coordinator' | 'director';
/** Lowest to highest. Used when one person holds several roles on one company. */
const TIER_RANK: Record<Tier, number> = { field: 0, coordinator: 1, director: 2 };

// Role-scoped default landing / nav (Lane A contract). The `primary_module` JWT
// claim names the operator's own module, matching the module slug exactly.
// SAFE FALLBACK: until Lane A mints tokens that carry this claim, usePrimaryModule
// returns null and every consumer keeps its existing behaviour unchanged.
export type PrimaryModule = 'sales' | 'property' | 'production' | 'finance' | 'inventory' | 'capture';

// "View As" role preview (staging-only, client-only UI state). The three
// preview roles map onto the app's tier model: director→director,
// manager→coordinator, operator→field. `null` = no preview (the admin sees
// their own real role). See session.tsx provider + ViewAsSwitcher.
export type ViewAsRole = 'director' | 'manager' | 'operator' | null;

function tierForViewAs(r: Exclude<ViewAsRole, null>): Tier {
  if (r === 'director') return 'director';
  if (r === 'manager') return 'coordinator';
  return 'field';
}

export interface EntityAssignment {
  entity_id: string;
  entity_name: string;
  entity_slug: string;
  tenant_id: string;
  tenant_name: string;
  role_slug: string;
  role_name: string;
  tier: Tier;
  feature_permissions: string[];
}

// Keep backward-compatible SbuAssignment
export interface SbuAssignment {
  tenant_id: string;
  tenant_name: string;
  role_slug: string;
  role_name: string;
  tier: Tier;
  feature_permissions: string[];
}

export interface Session {
  tenantId: string;
  entityId: string;
  userId: string;
  email: string;
  fullName: string;
  tier: Tier;
  isPlatformAdmin: boolean;
  assignments: SbuAssignment[];
  entityAssignments: EntityAssignment[];
  permissions: string[];
  can: (perm: string) => boolean;
  contextMode: ContextMode;          // 'user' = aggregate across all my entities + personal; 'entity' = scoped
  switchSbu: (tenantId: string) => void;
  switchEntity: (entityId: string) => void;
  switchToUser: () => void;          // enter User mode (self, all affiliated entities)
  ready: boolean;
  // True when the access lookup timed out or failed and the session below is the fail-closed
  // fallback rather than this person's real access. Screens stay usable; the shell says so.
  sessionError: boolean;

  // ── "View As" role preview (A — UX preview, staging-only) ──────────────────
  // CLIENT-ONLY, IN-MEMORY UI state. NEVER sent to an API, NEVER used in a data
  // fetch/RPC param, NEVER persisted as auth — RLS is the real wall; this only
  // drives nav/guard/landing gating. Resets on reload. `tier`/`isPlatformAdmin`
  // above stay the REAL role; UI gating reads the `effective*` fields instead.
  viewAsRole: ViewAsRole;                 // active preview role, or null. Forced null off-staging or for non-admins.
  setViewAsRole: (r: ViewAsRole) => void; // no-op unless a real admin on a non-prod host
  effectiveTier: Tier;                    // = preview tier ?? real tier
  effectiveIsPlatformAdmin: boolean;      // preview can only DROP platform-admin, never grant it
}

// The resolved session (everything except the view-as overlay, which the
// provider layers on top so a preview toggle never re-runs the DB resolve).
type ResolvedSession = Omit<Session, 'viewAsRole' | 'setViewAsRole' | 'effectiveTier' | 'effectiveIsPlatformAdmin'>;

/** The role a guard/nav should gate on — the effective (preview-aware) role. */
export function effectiveRoleView(
  s: Pick<Session, 'effectiveTier' | 'effectiveIsPlatformAdmin'>,
): { tier: Tier; isPlatformAdmin: boolean } {
  return { tier: s.effectiveTier, isPlatformAdmin: s.effectiveIsPlatformAdmin };
}

// Pre-auth placeholder: empty ids + ready:false. Consumers treat ready:false /
// empty tenantId as a loading state — we never default to a specific SBU (this
// previously fell back to the Wellies tenant/a test user). Once the auth store
// has a user, resolve() replaces this with the live session below.
const fallbackSession: ResolvedSession = {
  tenantId: '',
  entityId: '',
  userId: '',
  email: '',
  fullName: '',
  tier: 'director',
  isPlatformAdmin: false,
  assignments: [],
  entityAssignments: [],
  permissions: ['all'],
  can: () => true,
  contextMode: 'entity',
  switchSbu: () => {},
  switchEntity: () => {},
  switchToUser: () => {},
  ready: false,
  sessionError: false,
};

// 🔴 WHAT WE BECOME WHEN WE CANNOT READ THIS PERSON'S ACCESS — and it must be the LEAST we
// could be, never the most (16 Sep). The error path used to hand out `fallbackSession` as it
// stands above: tier 'director', permissions ['all'], can() true. So one failed or stalled read
// of v_rainmaker_user_access turned a salesperson into a manager in the interface — the module
// rail, the quick-action menu and every RequireManager route, including the invoice register the
// outside review found. RLS still refused the data, so nothing leaked; but the guards that are
// the whole point of the role work were one dropped request away from opening.
//
// Deny by default: the lowest tier, no permissions, can() false, and `sessionError` so the shell
// can say plainly what happened instead of leaving a spinner turning (R-01, R-02).
const deniedSession = (userId: string): ResolvedSession => ({
  ...fallbackSession,
  userId,
  tier: 'field',
  permissions: [],
  can: () => false,
  ready: true,
  sessionError: true,
});

const SessionCtx = createContext<Session>({
  ...fallbackSession,
  viewAsRole: null,
  setViewAsRole: () => {},
  effectiveTier: fallbackSession.tier,
  effectiveIsPlatformAdmin: fallbackSession.isPlatformAdmin,
});

interface JwtClaims {
  sub?: string;
  email?: string;
  app_user_id?: string;
  tenant_id?: string;
  entity_id?: string;
  app_role?: string;
  tier?: Tier;
  is_platform_admin?: boolean;
  permissions?: string[];
  entitlements?: string[];
  primary_module?: PrimaryModule;
}

function decodeJwtPayload(token: string): JwtClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const authUser = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [session, setSession] = useState<ResolvedSession>(fallbackSession);
  // View-as is a separate, in-memory overlay (never persisted). Kept out of the
  // resolved session so toggling it never re-runs resolve().
  const [viewAsRole, setViewAsRoleState] = useState<ViewAsRole>(null);

  useEffect(() => {
    if (!authUser?.id) return;
    resolve(authUser.id, authUser.tenantId);
  }, [authUser?.id]);

  // A preview may be SET only by a REAL admin (real director or platform admin)
  // on a non-prod host. This is the single security gate: computed from the REAL
  // role, so it can never be widened by the preview itself.
  const realIsAdmin = session.isPlatformAdmin || session.tier === 'director';
  const viewAsAllowed = session.ready && realIsAdmin && !isProdHost();

  const setViewAsRole = useCallback((r: ViewAsRole) => {
    // Ignore any attempt to set a preview when not permitted (non-admin, prod
    // host, or not yet ready) — even via direct state. Clearing (null) is always fine.
    if (r !== null && !(session.ready && (session.isPlatformAdmin || session.tier === 'director') && !isProdHost())) return;
    console.info('[view-as]', r);
    setViewAsRoleState(r);
  }, [session.ready, session.isPlatformAdmin, session.tier]);

  // effectiveViewAsRole is null unless a preview is both set AND allowed — so a
  // stale/forced value can never leak privilege. Preview only ever DE-escalates:
  // it maps to a tier and forces isPlatformAdmin false.
  const effectiveViewAsRole: ViewAsRole = viewAsAllowed ? viewAsRole : null;
  const effectiveTier: Tier = effectiveViewAsRole ? tierForViewAs(effectiveViewAsRole) : session.tier;
  const effectiveIsPlatformAdmin = effectiveViewAsRole ? false : session.isPlatformAdmin;

  const value: Session = {
    ...session,
    viewAsRole: effectiveViewAsRole,
    setViewAsRole,
    effectiveTier,
    effectiveIsPlatformAdmin,
  };

  async function resolve(userId: string, authTenantId: string) {
    // 🔴 BOUND THE WAIT (R-01, R-02, 16 Sep). These reads had no timeout, and every screen waits on
    // `ready`: one request that never settles left Today, My targets and /sales showing "Loading…"
    // for as long as the reviewer was willing to sit there. Reproduced on the rig by holding
    // v_rainmaker_user_access open — all three hung indefinitely. After 10 seconds we stop waiting
    // and deny, so a phone on a bad line gets a sentence instead of a spinner. If the real answer
    // arrives afterwards the success path below still replaces this, so it heals itself.
    const stallTimer = setTimeout(() => {
      setSession(prev => (prev.ready ? prev : deniedSession(userId)));
    }, 10000);
    try {
      const claims = accessToken ? decodeJwtPayload(accessToken) : null;
      const hasCustomClaims = claims?.tenant_id && claims?.app_role;

      const [rows, userRows] = await Promise.all([
        sandbox<{
          user_id: string; email: string; full_name: string;
          tenant_id: string; tenant_name: string;
          entity_id: string; entity_name: string; entity_slug: string;
          role_slug: string; role_name: string; tier: string;
          feature_permissions: string[];
          access_status: string;
          granted_at: string | null;
        }>('v_rainmaker_user_access', `user_id=eq.${userId}&access_status=eq.active`),
        hasCustomClaims
          ? Promise.resolve([])
          : sandbox<{ is_platform_admin: boolean }>('rainmaker_users', `id=eq.${userId}&select=is_platform_admin`),
      ]);

      // 🔴 WHICH COMPANY YOU LAND IN MUST NOT BE LUCK (17 Sep). With no remembered company, the
      // app takes entityAssignments[0] — and the view returns rows in no guaranteed order, so a
      // multi-company user (Ravi, Jody) could sign in twice and land in two different companies,
      // reading two different sets of numbers without noticing. Found while chasing a test that
      // "failed" twice and passed once for exactly this reason.
      // Oldest grant first, then by company name: stable across sessions, and the company someone
      // has belonged to longest is the sensible default. A remembered per-user default is the
      // proper fix and is Lane A's after the site week; this makes it deterministic today.
      const ordered = [...rows].sort((a, b) =>
        (a.granted_at ?? '').localeCompare(b.granted_at ?? '') ||
        (a.entity_name ?? '').localeCompare(b.entity_name ?? ''));
      const entityAssignments: EntityAssignment[] = ordered.map((r) => ({
        entity_id: r.entity_id,
        entity_name: r.entity_name ?? r.tenant_name,
        entity_slug: r.entity_slug ?? '',
        tenant_id: r.tenant_id,
        tenant_name: r.tenant_name,
        role_slug: r.role_slug,
        role_name: r.role_name,
        tier: tierFromRow(r.tier, r.role_slug),
        feature_permissions: Array.isArray(r.feature_permissions) ? r.feature_permissions : [],
      }));

      // Backward-compatible assignments (deduplicated by tenant)
      const seenTenants = new Set<string>();
      const assignments: SbuAssignment[] = [];
      for (const ea of entityAssignments) {
        if (!seenTenants.has(ea.tenant_id)) {
          seenTenants.add(ea.tenant_id);
          assignments.push({
            tenant_id: ea.tenant_id,
            tenant_name: ea.tenant_name,
            role_slug: ea.role_slug,
            role_name: ea.role_name,
            tier: ea.tier,
            feature_permissions: ea.feature_permissions,
          });
        }
      }

      const savedEntity = localStorage.getItem(ENTITY_KEY);
      const savedSbu = localStorage.getItem(SBU_KEY);

      const resolveEntityId = (): string => {
        if (savedEntity && entityAssignments.some((a) => a.entity_id === savedEntity)) return savedEntity;
        if (claims?.entity_id) return claims.entity_id;
        return entityAssignments[0]?.entity_id ?? '';
      };

      const activeEntityId = resolveEntityId();
      const activeAssignment = entityAssignments.find((a) => a.entity_id === activeEntityId);

      // Resolve the active tenant from the live session, in priority order:
      // active entity's tenant → saved SBU → JWT claim → first assignment →
      // the auth store's tenant for this user. Falls back to '' (never a
      // hardcoded SBU) so an unresolved session renders empty, not as Wellies.
      const tenantId = activeAssignment?.tenant_id
        ?? (savedSbu && assignments.some((a) => a.tenant_id === savedSbu) ? savedSbu : null)
        ?? claims?.tenant_id
        ?? assignments[0]?.tenant_id
        ?? authTenantId
        ?? '';

      const allPerms = [...new Set(entityAssignments.flatMap((a) => a.feature_permissions))];
      const isPlatformAdmin = hasCustomClaims
        ? (claims.is_platform_admin ?? false)
        : (userRows[0]?.is_platform_admin ?? false);

      const buildSession = (eid: string, tid: string): ResolvedSession => {
        // 🔴 A PERSON CAN HOLD MORE THAN ONE ROLE ON ONE COMPANY (21 Sep). This used to take
        // `.find()` — the FIRST assignment for the entity — and read permissions from that alone.
        // `ordered` is sorted oldest grant first, so the OLDEST role won and every later one was
        // ignored: promoting someone did nothing until their previous grant was revoked. Found
        // when Ravi was granted Super User on all 12 companies (0354) and still could not see the
        // Invite button on Wellies, because his July Director grant sorted ahead of it.
        // The entity's permissions are the UNION of every live role the person holds there.
        const eas = entityAssignments.filter((a) => a.entity_id === eid);
        const ea = eas[0];
        const entityPerms = eas.length
          ? [...new Set(eas.flatMap((a) => a.feature_permissions))]
          : allPerms;
        return {
          tenantId: tid,
          entityId: eid,
          userId: hasCustomClaims ? (claims.app_user_id ?? userId) : userId,
          email: hasCustomClaims ? (claims.email ?? '') : (rows[0]?.email ?? ''),
          fullName: rows[0]?.full_name ?? '',
          // Same rule as permissions: with two roles on one company, take the HIGHEST tier, not
          // the oldest grant. tier decides the role badge and which actions the mobile sheet
          // offers, so the old behaviour handed a promoted person the field surface.
          tier: (eas.length
            ? eas.reduce<Tier>((best, a) =>
                TIER_RANK[(a.tier as Tier) ?? 'field'] > TIER_RANK[best] ? ((a.tier as Tier) ?? 'field') : best, 'field')
            : ((claims?.tier as Tier) ?? 'field')),
          isPlatformAdmin,
          assignments,
          entityAssignments,
          permissions: entityPerms,
          can: (p) => {
            // A platform admin may operate every control (Ravi, 21 Sep). isPlatformAdmin was
            // computed here and then never consulted, so the flag showed in the header and bought
            // nothing. This is a UI gate ONLY — what any signed-in user may actually read or write
            // is still decided by RLS at the database, which does not consult this flag.
            if (isPlatformAdmin) return true;
            return entityPerms.includes('all') || entityPerms.includes(p);
          },
          contextMode: readContextMode(),
          switchToUser: () => {
            localStorage.setItem(CONTEXT_KEY, 'user');
            setSession(buildSession(eid, tid));
          },
          switchSbu: (newTid) => {
            localStorage.setItem(SBU_KEY, newTid);
            const firstEntityInTenant = entityAssignments.find((a) => a.tenant_id === newTid);
            if (firstEntityInTenant) {
              localStorage.setItem(ENTITY_KEY, firstEntityInTenant.entity_id);
              setSession(buildSession(firstEntityInTenant.entity_id, newTid));
            } else {
              setSession((prev) => ({ ...prev, tenantId: newTid }));
            }
          },
          switchEntity: (newEid) => {
            localStorage.setItem(CONTEXT_KEY, 'entity');
            localStorage.setItem(ENTITY_KEY, newEid);
            const ea2 = entityAssignments.find((a) => a.entity_id === newEid);
            if (ea2) {
              localStorage.setItem(SBU_KEY, ea2.tenant_id);
              setSession(buildSession(newEid, ea2.tenant_id));
            }
          },
          ready: true,
          sessionError: false,
        };
      };

      clearTimeout(stallTimer);
      setSession(buildSession(activeEntityId, tenantId));
    } catch {
      // Deny, never promote. This line used to spread fallbackSession, which is a director.
      clearTimeout(stallTimer);
      setSession(deniedSession(userId));
    }
  }

  return (
    <SessionCtx.Provider value={value}>
      {/* One bar, in one place, for every screen: when we could not read this person's access we
          show the least we could — so the app would otherwise look mysteriously empty. Say it
          plainly and give him the one action that helps. */}
      {value.sessionError && (
        <div role="status" className="fixed top-0 inset-x-0 z-[100] bg-[#fffbeb] border-b border-[#fde68a] px-3 py-2 text-[13px] text-[#92400e] flex items-center justify-between gap-3">
          <span>We couldn’t load what you’re allowed to see, so some screens will look empty.</span>
          <button onClick={() => window.location.reload()}
            className="shrink-0 rounded-[8px] border border-[#92400e] px-2.5 py-1 text-[12px] font-semibold">
            Try again
          </button>
        </div>
      )}
      {children}
    </SessionCtx.Provider>
  );
}

export function useSession(): Session {
  return useContext(SessionCtx);
}

/**
 * Read the `primary_module` claim from the current session's access token.
 * Returns 'sales' | 'property' when the JWT carries a recognised value, else
 * null — the claim is absent (today's case, pre-Lane-A) OR its value is not one
 * we map. NEVER inferred from role/entity: this only surfaces the raw claim, so
 * the role-scoped landing/nav stays completely inert until the token carries it.
 */
export function usePrimaryModule(): PrimaryModule | null {
  const accessToken = useAuthStore((s) => s.accessToken);
  const { entityAssignments, entityId } = useContext(SessionCtx);
  const pm = accessToken ? decodeJwtPayload(accessToken)?.primary_module : undefined;
  const known: PrimaryModule[] = ['sales', 'property', 'production', 'finance', 'inventory', 'capture'];
  if (known.includes(pm as PrimaryModule)) return pm as PrimaryModule;
  // 🔴 FALL BACK TO HIS ROLE WHEN THE CLAIM IS MISSING (R-09, 16 Sep). The claim is not on every
  // seat yet, and without it a salesperson fell through to the director's portfolio — the outside
  // review saw "Portfolio — 3Wi Group" with six blank cards on a sales seat, and my own guards
  // send a refused person to /home, so they were walking him there too. His role in the active
  // company already says where he belongs; only the two roles that own a cockpit are mapped, so
  // nobody else's landing moves.
  const slug = entityAssignments.find((a) => a.entity_id === entityId)?.role_slug ?? '';
  if (slug === 'sales_operator') return 'sales';
  if (slug === 'production_operator') return 'production';
  return null;
}

// The database owns tiers: `rainmaker_roles.tier`, exposed as `v_rainmaker_user_access.tier` and already
// fetched above. `tierFromSlug` was written on 1 Jul, before that column was read (7 Jul), and was never
// switched over — so any role added later fell through to 'field'. `entity_admin`, the office role, is
// 'coordinator' in the database and was rendered field-tier everywhere on live: found 14 Sep when the
// Wellies office (Saisha, Ashnee) got the read-only view of Receipts and bank match. Of live's 12 roles
// it was the only one the slug map got wrong.
// Trust the database value when it is one of our tiers; the slug map stays only as a fallback for a
// missing or unrecognised value. UI gating only — RLS and the RPCs never read this.
const DB_TIERS: readonly Tier[] = ['field', 'coordinator', 'director'];
function tierFromRow(dbTier: string | null | undefined, slug: string): Tier {
  // 🔴 finance_clerk (0563) is 'coordinator' tier in the database only because that is how the role was carried (so it is neither a field nor
  // a restricted seat). In THIS app a coordinator tier means MANAGER: the full menu and every RequireManager screen (suppliers with bank
  // details, bank imports, arrears). A capturer who files slips and approves nothing must not inherit that the day her seat is moved, so the
  // screens treat her as field tier, exactly as her field_operator seat is treated today. UI gating only: the database never reads this.
  if (slug === 'finance_clerk') return 'field';
  return DB_TIERS.includes(dbTier as Tier) ? (dbTier as Tier) : tierFromSlug(slug);
}

function tierFromSlug(slug: string): Tier {
  if (slug === 'super_user' || slug === 'director') return 'director';
  if (slug.startsWith('coordinator') || slug === 'auditor' || slug === 'hempire_psc') return 'coordinator';
  return 'field';
}
